/**
 * Number to Words Converter
 *
 * This utility provides number to words conversion for both English and Arabic.
 *
 * Installation required:
 * pnpm add number-to-words number-to-arabic-words
 *
 * Once installed, uncomment the imports and use the library functions.
 */

// Uncomment these after installing the packages:
// import { toWords } from 'number-to-words';
// import { NumberToArabicWords } from 'number-to-arabic-words';

/**
 * Convert number to English words
 * @param {number} num - The number to convert
 * @returns {string} - The number in English words
 */
export const numberToEnglishWords = (num) => {
    try {
        // Uncomment after installing package:
        // return toWords(num);

        // Temporary fallback - basic implementation
        return convertToEnglishWordsBasic(num);
    } catch (error) {
        console.error("Error converting to English words:", error);
        return num.toString();
    }
};

/**
 * Convert number to Arabic words
 * @param {number} num - The number to convert
 * @returns {string} - The number in Arabic words
 */
export const numberToArabicWords = (num) => {
    try {
        // Uncomment after installing package:
        // return NumberToArabicWords(num);

        // Temporary fallback
        return convertToArabicWordsBasic(num);
    } catch (error) {
        console.error("Error converting to Arabic words:", error);
        return num.toString();
    }
};

/**
 * Convert number to words with currency
 * @param {number} num - The number to convert
 * @param {boolean} isArabic - Whether to use Arabic or English
 * @param {string} currency - Currency name (default: 'dollars')
 * @param {number} exchangeRate - Exchange rate to LBP (optional)
 * @returns {string} - The formatted string
 */
export const numberToWordsWithCurrency = (
    num,
    isArabic = false,
    currency = "dollars",
    exchangeRate = null
) => {
    if (!num || isNaN(num)) return "";

    const amount = parseFloat(num);
    const [whole, decimal] = amount.toFixed(2).split(".");

    let result = "";

    if (isArabic) {
        const wholeWords = numberToArabicWords(parseInt(whole));
        const decimalWords =
            decimal && parseInt(decimal) > 0
                ? ` و ${numberToArabicWords(parseInt(decimal))} سنت`
                : "";
        result = `${wholeWords} دولار${decimalWords}`;
    } else {
        const wholeWords = numberToEnglishWords(parseInt(whole));
        const decimalWords =
            decimal && parseInt(decimal) > 0
                ? ` and ${numberToEnglishWords(parseInt(decimal))} cents`
                : "";
        result = `${wholeWords} ${currency}${decimalWords}`;
    }

    // Capitalize first letter
    return result.charAt(0).toUpperCase() + result.slice(1);
}; // ============= BASIC IMPLEMENTATIONS (Fallback) =============
// These are simple implementations. Replace with library functions after installation.

function convertToEnglishWordsBasic(num) {
    const ones = [
        "",
        "one",
        "two",
        "three",
        "four",
        "five",
        "six",
        "seven",
        "eight",
        "nine",
    ];
    const tens = [
        "",
        "",
        "twenty",
        "thirty",
        "forty",
        "fifty",
        "sixty",
        "seventy",
        "eighty",
        "ninety",
    ];
    const teens = [
        "ten",
        "eleven",
        "twelve",
        "thirteen",
        "fourteen",
        "fifteen",
        "sixteen",
        "seventeen",
        "eighteen",
        "nineteen",
    ];

    if (num === 0) return "zero";
    if (num < 0) return "minus " + convertToEnglishWordsBasic(Math.abs(num));

    let words = "";

    if (Math.floor(num / 1000000) > 0) {
        words +=
            convertToEnglishWordsBasic(Math.floor(num / 1000000)) + " million ";
        num %= 1000000;
    }

    if (Math.floor(num / 1000) > 0) {
        words +=
            convertToEnglishWordsBasic(Math.floor(num / 1000)) + " thousand ";
        num %= 1000;
    }

    if (Math.floor(num / 100) > 0) {
        words += ones[Math.floor(num / 100)] + " hundred ";
        num %= 100;
    }

    if (num > 0) {
        if (num < 10) {
            words += ones[num];
        } else if (num < 20) {
            words += teens[num - 10];
        } else {
            words += tens[Math.floor(num / 10)];
            if (num % 10 > 0) {
                words += "-" + ones[num % 10];
            }
        }
    }

    return words.trim();
}

function convertToArabicWordsBasic(num) {
    const ones = [
        "",
        "واحد",
        "اثنان",
        "ثلاثة",
        "أربعة",
        "خمسة",
        "ستة",
        "سبعة",
        "ثمانية",
        "تسعة",
    ];
    const tens = [
        "",
        "عشرة",
        "عشرون",
        "ثلاثون",
        "أربعون",
        "خمسون",
        "ستون",
        "سبعون",
        "ثمانون",
        "تسعون",
    ];
    const teens = [
        "عشرة",
        "أحد عشر",
        "اثنا عشر",
        "ثلاثة عشر",
        "أربعة عشر",
        "خمسة عشر",
        "ستة عشر",
        "سبعة عشر",
        "ثمانية عشر",
        "تسعة عشر",
    ];
    const hundreds = [
        "",
        "مئة",
        "مئتان",
        "ثلاثمئة",
        "أربعمئة",
        "خمسمئة",
        "ستمئة",
        "سبعمئة",
        "ثمانمئة",
        "تسعمئة",
    ];

    if (num === 0) return "صفر";
    if (num < 0) return "سالب " + convertToArabicWordsBasic(Math.abs(num));

    let words = "";

    if (Math.floor(num / 1000000) > 0) {
        words +=
            convertToArabicWordsBasic(Math.floor(num / 1000000)) + " مليون ";
        num %= 1000000;
    }

    if (Math.floor(num / 1000) > 0) {
        words += convertToArabicWordsBasic(Math.floor(num / 1000)) + " ألف ";
        num %= 1000;
    }

    if (Math.floor(num / 100) > 0) {
        words += hundreds[Math.floor(num / 100)];
        num %= 100;
        if (num > 0) {
            words += " و ";
        }
    }

    if (num > 0) {
        if (num < 10) {
            words += ones[num];
        } else if (num < 20) {
            words += teens[num - 10];
        } else {
            if (num % 10 > 0) {
                words += ones[num % 10] + " و ";
            }
            words += tens[Math.floor(num / 10)];
        }
    }

    return words.trim();
}
